/**
 * Form (conteúdo de slide-over) para criar NFS-e — M12.1, redesign FIN-9.
 *
 * Renderizado dentro de um SlideOverPanel pela Lista. Backend
 * POST /api/notas-fiscais/criar-nfse (M6.A.3): valida
 * ConfiguracaoFiscal.inscricao_municipal, cria nota tipo=NFSE PENDENTE com
 * snapshot do código+alíquota+descrição. Gestor clica "Emitir" no detalhe.
 */
import React, { useEffect, useState } from "react";
import { FiFilePlus } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import { useAuth } from "../../../context/AuthContext";
import { criarNotaFiscalServico } from "../../../services/fiscal";
import { parseDinheiro } from "../../../utils/dinheiro";
import { Input, Select, Textarea } from "../../Orcamentos/styles";

export default function CriarNfseForm({ onCreated, onCancel }) {
  const { request } = useAuth();
  const [pedidos, setPedidos] = useState([]);
  const [loadingPedidos, setLoadingPedidos] = useState(false);
  const [d, setD] = useState({
    pedido_id: "", codigo_servico: "", descricao_servico: "",
    aliquota_iss: "", valor_total: "",
  });
  const set = (k, v) => setD((p) => ({ ...p, [k]: v }));

  useEffect(() => {
    setLoadingPedidos(true);
    // Decisão D4 (auditoria 2026-08-07): a nota acompanha a MERCADORIA, então
    // o documento é elegível já na expedição — exigir "entregue" deixava o
    // dropdown vazio (nenhum pedido chegava a ENTREGUE) e o módulo fiscal
    // inteiro inutilizável.
    Promise.all([
      request("/api/pedidos?situacao=EM_EXPEDICAO").catch(() => []),
      request("/api/pedidos?situacao=ENTREGUE").catch(() => []),
    ])
      .then(([emExpedicao, entregues]) => {
        const lista = (p) => (Array.isArray(p) ? p : p?.results || []);
        setPedidos([...lista(emExpedicao), ...lista(entregues)]);
      })
      .catch(() => {})
      .finally(() => setLoadingPedidos(false));
  }, [request]);

  const valido = d.pedido_id && d.codigo_servico.trim() && d.descricao_servico.trim().length >= 10;

  const submit = async () => {
    const payload = {
      pedido_id: d.pedido_id,
      codigo_servico: d.codigo_servico.trim(),
      descricao_servico: d.descricao_servico.trim(),
    };
    // A22: quando o pedido já tem documento fiscal ativo, o backend exige o
    // valor desta nota — dois documentos declarando 100% da mesma venda é
    // exposição fiscal. Em branco, o backend usa o valor do pedido.
    if (d.valor_total) {
      const v = parseDinheiro(d.valor_total);
      if (!v || v <= 0) throw new Error("Valor da nota deve ser maior que zero.");
      payload.valor_total = v;
    }
    if (d.aliquota_iss) {
      const a = parseFloat(d.aliquota_iss);
      if (isNaN(a) || a < 0 || a > 5) throw new Error("Alíquota ISS deve estar entre 0 e 5% (regra CDC).");
      payload.aliquota_iss = a;
    }
    const nova = await criarNotaFiscalServico(request, payload);
    onCreated?.(nova);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Pedido *</span>
        <Select value={d.pedido_id} onChange={(e) => set("pedido_id", e.target.value)}>
          <option value="">{loadingPedidos ? "Carregando…" : "— selecione o pedido —"}</option>
          {pedidos.map((p) => (
            <option key={p.id} value={p.id}>
              {p.numero} · {p.cliente_nome} ({p.situacao_display || p.situacao})
            </option>
          ))}
        </Select>
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Pedidos em expedição ou já entregues — a nota acompanha a mercadoria.
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Código de serviço (LC 116/2003) *</span>
        <Input value={d.codigo_servico} maxLength={20}
               onChange={(e) => set("codigo_servico", e.target.value)}
               placeholder="ex.: 14.05" />
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Marcenaria: 14.05 (montagem), 14.06 (fabricação sob encomenda), 7.02 (pintura/acabamento).
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Descrição do serviço *</span>
        <Textarea value={d.descricao_servico} rows={4} maxLength={500}
                  onChange={(e) => set("descricao_servico", e.target.value)}
                  placeholder="ex.: Fabricação e instalação de móveis sob medida do pedido…" />
        <span className={"char-count" + (d.descricao_servico.trim().length >= 10 ? " ok" : "")}>
          {d.descricao_servico.trim().length}/10 mín · 500 máx
        </span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>Alíquota ISS (opcional)</span>
        <Input type="number" step="0.01" min="0" max="5" value={d.aliquota_iss}
               onChange={(e) => set("aliquota_iss", e.target.value)}
               placeholder="vazio = usa o padrão da configuração" />
        <span style={{ fontSize: 11, opacity: 0.6 }}>Máximo 5% (CDC).</span>
      </label>

      <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span style={{ fontSize: 12, fontWeight: 600 }}>
          Valor desta nota (opcional)
        </span>
        <Input value={d.valor_total}
               onChange={(e) => set("valor_total", e.target.value)}
               placeholder="vazio = valor total do pedido" />
        <span style={{ fontSize: 11, opacity: 0.6 }}>
          Só preencha se o pedido já tiver outra nota — a soma dos
          documentos não pode passar o valor do pedido.
        </span>
      </label>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button type="button" className="btn btn--secondary" onClick={onCancel}>Cancelar</button>
        <AsyncButton variant="primary" disabled={!valido}
                     successMessage="NFS-e criada — clique Emitir no detalhe"
                     onClick={submit}>
          <FiFilePlus size={14} aria-hidden="true" /> Criar NFS-e
        </AsyncButton>
      </div>
    </div>
  );
}
